export const useListHeaderSync = () => {
  let fileView = null;
  let listBodyTable = null;
  let boundScrollTarget = null;
  let scrollHandler = null;
  let resizeObserver = null;
  let bodyMutationObserver = null;
  let windowResizeHandler = null;
  let rafId = null;

  // 将来ページ内に別の .table-responsive が増えても誤取得しないよう .file-view 配下に限定する
  const getElements = () => {
    const fileViewEl = document.querySelector('.file-view');
    if (!fileViewEl) {
      return null;
    }

    const listHeaderWrap = fileViewEl.querySelector('.list-header-wrap');
    const listHeaderTable = fileViewEl.querySelector('.list-header-table');
    const tableResponsive = fileViewEl.querySelector('.table-responsive');
    const listBodyTableEl = fileViewEl.querySelector('.list-body-table');

    if (!listHeaderWrap || !listHeaderTable || !tableResponsive || !listBodyTableEl) {
      return null;
    }

    return { fileViewEl, listHeaderWrap, listHeaderTable, tableResponsive, listBodyTableEl };
  };

  // グリッド/リスト切替( v-if )のたびに Vue が .table-responsive を破棄・再生成するため、
  // 同期のたびに現在の要素を確認し、入れ替わっていればリスナーを張り替える
  const ensureScrollBinding = (tableResponsive) => {
    if (boundScrollTarget === tableResponsive) {
      return;
    }

    if (boundScrollTarget && scrollHandler) {
      boundScrollTarget.removeEventListener('scroll', scrollHandler);
    }

    scrollHandler = () => {
      // listHeaderWrap も再生成され得るため、発火のたびに取得し直す
      const elements = getElements();
      if (!elements) {
        return;
      }
      elements.listHeaderWrap.scrollLeft = elements.tableResponsive.scrollLeft;
    };

    tableResponsive.addEventListener('scroll', scrollHandler, { passive: true });
    boundScrollTarget = tableResponsive;
  };

  const syncListHeaderWidths = () => {
    const elements = getElements();
    if (!elements) {
      return;
    }

    const { listHeaderWrap, listHeaderTable, tableResponsive, listBodyTableEl } = elements;

    ensureScrollBinding(tableResponsive);

    // 1. ボディ側の縦スクロールバー幅ぶんヘッダーが右にはみ出すのを防ぐ
    //    padding だと内側テーブルがパディング領域に描画されてしまうため margin で相殺する
    //    オーバーレイスクロールバー環境では差が 0 になり自動的に無効化される
    //    自然幅測定より前に適用し、両テーブルを同じ有効幅で測定できるようにする
    const scrollbarWidth = tableResponsive.offsetWidth - tableResponsive.clientWidth;
    listHeaderWrap.style.marginRight = `${scrollbarWidth}px`;

    // 2. 測定のため両テーブルを一旦自然幅に戻す
    [listHeaderTable, listBodyTableEl].forEach((table) => {
      table.style.tableLayout = 'auto';
      table.style.width = '';
      table.querySelectorAll('col').forEach((col) => {
        col.style.width = '';
      });
    });

    // 3. ヘッダー各列の自然幅を測る
    const headerThs = listHeaderTable.querySelectorAll('thead th');
    const headerWidths = Array.from(headerThs).map((th) => th.getBoundingClientRect().width);

    // 4. ボディ各列の自然幅を測る（colspanを持つ行は測定に使わない）
    const measureRow = listBodyTableEl.querySelector('tbody tr.dashboard-row');
    let bodyWidths = [];
    if (measureRow) {
      const cells = measureRow.querySelectorAll('td');
      bodyWidths = Array.from(cells).map((td) => td.getBoundingClientRect().width);
    }

    // 5. 列ごとに確定幅を決定（列数はヘッダーのth数を基準にする）
    const columnCount = headerWidths.length;
    const finalWidths = [];
    for (let i = 0; i < columnCount; i += 1) {
      const headerWidth = headerWidths[i] || 0;
      const bodyWidth = bodyWidths[i] || 0;
      finalWidths.push(Math.ceil(Math.max(headerWidth, bodyWidth)));
    }

    const totalWidth = finalWidths.reduce((sum, w) => sum + w, 0);

    // 6. 適用
    [listHeaderTable, listBodyTableEl].forEach((table) => {
      table.style.tableLayout = 'fixed';
      table.style.width = `${totalWidth}px`;
      const cols = table.querySelectorAll('col');
      cols.forEach((col, i) => {
        if (finalWidths[i] !== undefined) {
          col.style.width = `${finalWidths[i]}px`;
        }
      });
    });
  };

  const requestSync = () => {
    if (rafId) {
      cancelAnimationFrame(rafId);
    }
    rafId = requestAnimationFrame(() => {
      rafId = null;
      syncListHeaderWidths();
    });
  };

  const initListHeaderSync = () => {
    teardownListHeaderSync();

    fileView = document.querySelector('.file-view');
    const fileViewBody = fileView ? fileView.querySelector('.file-view-body') : null;
    listBodyTable = fileView ? fileView.querySelector('.list-body-table') : null;

    // 初期表示時の幅同期・横スクロール同期のバインドを行う
    requestSync();

    if (typeof window.ResizeObserver === 'function') {
      resizeObserver = new window.ResizeObserver(() => {
        requestSync();
      });
      if (fileView) {
        resizeObserver.observe(fileView);
      }
      if (listBodyTable) {
        resizeObserver.observe(listBodyTable);
      }
    }

    if (fileViewBody) {
      bodyMutationObserver = new MutationObserver(() => {
        requestSync();
      });
      bodyMutationObserver.observe(fileViewBody, { childList: true, subtree: true });
    }

    windowResizeHandler = () => {
      requestSync();
    };
    window.addEventListener('resize', windowResizeHandler);
  };

  const teardownListHeaderSync = () => {
    if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }

    if (boundScrollTarget && scrollHandler) {
      boundScrollTarget.removeEventListener('scroll', scrollHandler);
    }
    boundScrollTarget = null;
    scrollHandler = null;

    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    }

    if (bodyMutationObserver) {
      bodyMutationObserver.disconnect();
      bodyMutationObserver = null;
    }

    if (windowResizeHandler) {
      window.removeEventListener('resize', windowResizeHandler);
      windowResizeHandler = null;
    }

    fileView = null;
    listBodyTable = null;
  };

  return {
    initListHeaderSync,
    teardownListHeaderSync,
    syncListHeaderWidths,
  };
};
